import { useCallback, useState } from "react";
import toast from "react-hot-toast";

import { ZipFileEntry, downloadFilesAsZip } from "../utils/downloadFilesAsZip";
import {
  canSaveToDirectory,
  pickSaveDirectory,
  saveFilesToDirectory,
} from "../utils/saveFilesToDirectory";
import type { BulkFetchOptions } from "../utils/resumableDownload";
import {
  canStreamZipToDisk,
  pickZipSaveTarget,
  streamFilesAsZip,
} from "../utils/streamFilesAsZip";
import useUnloadGuard from "./useUnloadGuard";
import useWakeLock from "./useWakeLock";

/** 保存方法。zip: 1 つの zip ファイル / folder: フォルダ構成のまま保存（続きから再開できる） */
export type BulkDownloadMode = "zip" | "folder";

// 一括ダウンロードの状態。
// - choose:  保存方法の選択待ち（大きいダウンロードで、フォルダ保存に対応したブラウザのみ）
// - running: 取得中（処理済み件数 / 総数。reconnecting は通信が途切れて取り直している間 true）
// - partial: 一部のファイルを取得できなかった（成功・失敗件数）
// - error:   1件も取得できず失敗
// 全件成功時はモーダルを表示せず自動で閉じるため、ここには含めない。
export type BulkDownloadState =
  | { phase: "choose"; chooseZip: () => void; chooseFolder: () => void }
  | {
      phase: "running";
      mode: BulkDownloadMode;
      processed: number;
      total: number;
      reconnecting?: boolean;
    }
  | {
      phase: "partial";
      mode: BulkDownloadMode;
      succeeded: number;
      failed: number;
      total: number;
    }
  | { phase: "error"; mode: BulkDownloadMode };

/** ダウンロードの対象。配列か、配下全件を解決する関数（遅延ロード時。URL の取り直しにも使う） */
export type BulkDownloadSource = ZipFileEntry[] | (() => Promise<ZipFileEntry[]>);

/**
 * メモリ上で zip 化する方式（JSZip）で扱える合計サイズの目安。
 * これを超えるとブラウザのメモリ上限や zip の 4 GB 上限で失敗するため、
 * ディスクへ直接書き出せない（showSaveFilePicker 非対応の）ブラウザでは実行前に止める。
 */
const IN_MEMORY_ZIP_MAX_BYTES = 1.5 * 1024 ** 3;

/** これより小さいと分かっているダウンロードは、保存方法を聞かずに zip にする */
const CHOOSE_MODE_MIN_BYTES = 1024 ** 3;

const knownTotalBytes = (entries: ZipFileEntry[]): number =>
  entries.reduce(
    (sum, entry) => sum + ("file" in entry ? entry.file.size ?? 0 : entry.blob.size),
    0,
  );

const hasUnknownSize = (entries: ZipFileEntry[]): boolean =>
  entries.some((entry) => "file" in entry && entry.file.size == null);

type SaveTarget =
  | { mode: "zip"; writable: FileSystemWritableFileStream | null }
  | { mode: "folder"; directory: FileSystemDirectoryHandle };

/**
 * 一括ダウンロード（zip / フォルダ保存）の実行と進捗状態を管理するフック。
 *
 * Chrome / Edge では保存先を選ばせ、ディスクへ直接書き出す（サイズ上限なし）。
 * 大きいダウンロード（1 GB 以上、または配下を後から解決するフォルダ）では、先に保存方法を選ばせる:
 * - zip: 1 つの zip ファイル。途中で止まると最初からやり直し
 * - folder: フォルダ構成のまま保存。同じ保存先で再実行すると、保存済みのファイルを飛ばして続きから取る
 * それ以外のブラウザは従来どおりメモリ上で zip 化する（大きすぎる場合は実行前に案内する）。
 * どの方式でも、受信の途中で切れたファイルは続きから取り直す。
 *
 * 取得に成功したファイル ID は onFileDownloaded へ1件ずつ通知する（ダウンロード記録用）。
 * 全件成功時は state を自動でクリアし、一部失敗・全滅時は結果を state に残す
 * （BulkDownloadProgressModal で表示する想定）。
 * 取得中はスリープを止め、タブを閉じようとしたら確認を出す。
 *
 * startDownload(source, name) の name は拡張子なしのダウンロード名（zip は `<name>.zip`、
 * フォルダ保存は `<name>/` になる）。
 * startDownload はクリック直後に呼ぶこと。保存先ダイアログはユーザー操作の直後でないと
 * 開けないため、対象の解決（API 呼び出し）はダイアログの後にこのフック内で行う。
 */
export const useBulkDownload = (onFileDownloaded?: (fileId: string) => void) => {
  const [downloadState, setDownloadState] = useState<BulkDownloadState | null>(null);
  const isDownloading = downloadState?.phase === "running";
  const isBusy = isDownloading || downloadState?.phase === "choose";

  useUnloadGuard(isDownloading);
  useWakeLock(isDownloading);

  const run = useCallback(
    async (source: BulkDownloadSource, name: string, mode: BulkDownloadMode) => {
      const zipName = `${name}.zip`;
      // 保存先ダイアログはクリック直後に開く（ここより前に await を挟まない）
      let target: SaveTarget;
      try {
        if (mode === "folder") {
          const directory = await pickSaveDirectory();
          if (!directory) {
            setDownloadState(null);
            return;
          }
          target = { mode, directory };
        } else if (canStreamZipToDisk()) {
          const writable = await pickZipSaveTarget(zipName);
          if (!writable) {
            setDownloadState(null);
            return;
          }
          target = { mode, writable };
        } else {
          target = { mode, writable: null };
        }
      } catch {
        setDownloadState(null);
        toast.error("保存先を開けませんでした");
        return;
      }
      const writable = target.mode === "zip" ? target.writable : null;

      setDownloadState({ phase: "running", mode, processed: 0, total: 0 });

      let entries: ZipFileEntry[];
      try {
        entries = Array.isArray(source) ? source : await source();
      } catch {
        await writable?.abort();
        setDownloadState(null);
        toast.error("ダウンロード対象の取得に失敗しました");
        return;
      }
      if (entries.length === 0) {
        await writable?.abort();
        setDownloadState(null);
        toast.error("フォルダにファイルがありません");
        return;
      }
      if (target.mode === "zip" && !writable && knownTotalBytes(entries) > IN_MEMORY_ZIP_MAX_BYTES) {
        setDownloadState(null);
        toast.error(
          "容量が大きいため、このブラウザでは一括ダウンロードできません。Chrome または Edge でお試しください",
          { duration: 8000 },
        );
        return;
      }

      setDownloadState({ phase: "running", mode, processed: 0, total: entries.length });
      const onProgress = (processed: number, total: number) =>
        setDownloadState((prev) =>
          prev?.phase === "running" ? { ...prev, processed, total } : prev,
        );
      const onReconnectingChange = (reconnecting: boolean) =>
        setDownloadState((prev) =>
          prev?.phase === "running" ? { ...prev, reconnecting } : prev,
        );
      const fetchOptions: BulkFetchOptions = {
        onProgress,
        onReconnectingChange,
        refreshEntries: Array.isArray(source) ? undefined : source,
      };

      try {
        const result =
          target.mode === "folder"
            ? await saveFilesToDirectory(entries, target.directory, name, fetchOptions)
            : writable
              ? await streamFilesAsZip(entries, writable, fetchOptions)
              : await downloadFilesAsZip(entries, zipName, { onProgress });

        // 取得に成功したファイルのみ通知（ダウンロード既読登録など）
        if (onFileDownloaded) {
          result.succeededFileIds.forEach(onFileDownloaded);
        }

        if (result.succeeded === 0) {
          setDownloadState({ phase: "error", mode });
        } else if (result.failed > 0) {
          // 一部失敗 → 結果を知らせる
          setDownloadState({
            phase: "partial",
            mode,
            succeeded: result.succeeded,
            failed: result.failed,
            total: result.total,
          });
        } else {
          // 全件成功 → 結果モーダルは出さず自動で閉じる
          setDownloadState(null);
          if (target.mode === "folder") {
            toast.success(`「${name}」フォルダに保存しました`);
          }
        }
      } catch {
        setDownloadState({ phase: "error", mode });
      }
    },
    [onFileDownloaded],
  );

  const startDownload = useCallback(
    (source: BulkDownloadSource, name: string) => {
      if (isBusy || (Array.isArray(source) && source.length === 0)) return;

      const small =
        Array.isArray(source) &&
        !hasUnknownSize(source) &&
        knownTotalBytes(source) < CHOOSE_MODE_MIN_BYTES;
      if (!canSaveToDirectory() || small) {
        void run(source, name, "zip");
        return;
      }
      setDownloadState({
        phase: "choose",
        chooseZip: () => void run(source, name, "zip"),
        chooseFolder: () => void run(source, name, "folder"),
      });
    },
    [isBusy, run],
  );

  const closeDownloadModal = useCallback(() => setDownloadState(null), []);

  return { downloadState, isDownloading, startDownload, closeDownloadModal };
};
